/**
 * Cart state.
 *
 * The cart holds only item ids and quantities plus enough display data to render
 * the bar before the server replies. Prices in here are for display only —
 * `quote_order` and `place_order` recompute everything, and the checkout screen
 * overwrites these numbers with the server's as soon as a quote lands.
 *
 * The cart is scoped to one restaurant and persisted, because losing a
 * half-built order to an accidental refresh is the fastest way to lose a
 * customer.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { cartLineKey, withOptions, type CartLine, type CartOption, type PublicMenuItem } from '@yummy/shared';

const STORAGE_KEY = 'yummy.customer.cart.v1';

interface StoredCart {
  restaurantId: string | null;
  lines: CartLine[];
}

interface CartContextValue {
  restaurantId: string | null;
  lines: CartLine[];
  itemCount: number;
  /** Sum of line prices at the prices last seen from the server. */
  subtotalCents: number;
  /** Quantity of one dish with one set of choices, for rendering steppers. */
  quantityOf: (itemId: string, options?: CartOption[]) => number;
  add: (restaurantId: string, item: PublicMenuItem, options?: CartOption[]) => void;
  /** Adds several at once, for a sheet whose stepper picks the count. */
  addMany: (
    restaurantId: string,
    item: PublicMenuItem,
    options: CartOption[] | undefined,
    quantity: number,
  ) => void;
  setQuantity: (itemId: string, options: CartOption[], quantity: number) => void;
  remove: (itemId: string, options?: CartOption[]) => void;
  clear: () => void;
  /** True when the cart belongs to a different restaurant and would be replaced. */
  conflictsWith: (restaurantId: string) => boolean;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * Reads and mutates the cart.
 *
 * @returns The cart, its totals, and the mutators.
 * @throws Error when called outside {@link CartProvider}.
 */
export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart must be used inside CartProvider');
  return value;
}

function readStored(): StoredCart {
  if (typeof localStorage === 'undefined') return { restaurantId: null, lines: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { restaurantId: null, lines: [] };
    const parsed = JSON.parse(raw) as StoredCart;
    if (!parsed || !Array.isArray(parsed.lines)) return { restaurantId: null, lines: [] };
    return { restaurantId: parsed.restaurantId ?? null, lines: parsed.lines };
  } catch {
    return { restaurantId: null, lines: [] };
  }
}

/**
 * Holds the in-progress order.
 *
 * @param props Children.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<StoredCart>(readStored);

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage can be full or blocked; the cart still works in memory.
    }
  }, [state]);

  const addMany = useCallback(
    (
      restaurantId: string,
      item: PublicMenuItem,
      options: CartOption[] = [],
      added = 1,
    ) => {
      setState((current) => {
        // Switching restaurants starts a new cart rather than merging menus.
        const base: StoredCart =
          current.restaurantId === restaurantId ? current : { restaurantId, lines: [] };

        // The choices are the identity: adding a Large must not bump the Small
        // already in the cart, because they are different things to make.
        const key = cartLineKey(item.id, options);
        const existing = base.lines.find(
          (line) => cartLineKey(line.itemId, line.options) === key,
        );

        const lines = existing
          ? base.lines.map((line) =>
              cartLineKey(line.itemId, line.options) === key
                ? { ...line, quantity: Math.min(99, line.quantity + added) }
                : line,
            )
          : [
              ...base.lines,
              {
                itemId: item.id,
                name: item.name,
                // A portion is a choice now, so it comes through `options`.
                unitPriceCents: withOptions(item.effective_price_cents, options),
                // A dish sold in fives starts at five, not one, and a stepper
                // that asked for seven gets seven.
                quantity: Math.min(99, Math.max(added, item.min_quantity)),
                minQuantity: Math.max(1, item.min_quantity),
                photoUrl: item.photo_url,
                options,
              },
            ];

        return { restaurantId, lines };
      });
    },
    [],
  );

  const add = useCallback(
    (restaurantId: string, item: PublicMenuItem, options?: CartOption[]) =>
      addMany(restaurantId, item, options, 1),
    [addMany],
  );

  const setQuantity = useCallback(
    (itemId: string, options: CartOption[], quantity: number) => {
      const key = cartLineKey(itemId, options);
      const lineKey = (line: CartLine) => cartLineKey(line.itemId, line.options);
      setState((current) => {
        if (quantity <= 0) {
          const lines = current.lines.filter((line) => lineKey(line) !== key);
          return lines.length === 0 ? { restaurantId: null, lines: [] } : { ...current, lines };
        }
        return {
          ...current,
          lines: current.lines.map((line) =>
            lineKey(line) === key ? { ...line, quantity: Math.min(99, quantity) } : line,
          ),
        };
      });
    },
    [],
  );

  const remove = useCallback(
    (itemId: string, options: CartOption[] = []) => setQuantity(itemId, options, 0),
    [setQuantity],
  );

  const clear = useCallback(() => setState({ restaurantId: null, lines: [] }), []);

  const value = useMemo<CartContextValue>(() => {
    const itemCount = state.lines.reduce((sum, line) => sum + line.quantity, 0);
    const subtotalCents = state.lines.reduce(
      (sum, line) => sum + line.unitPriceCents * line.quantity,
      0,
    );

    return {
      restaurantId: state.restaurantId,
      lines: state.lines,
      itemCount,
      subtotalCents,
      quantityOf: (itemId: string, options: CartOption[] = []) =>
        state.lines.find((l) => cartLineKey(l.itemId, l.options) === cartLineKey(itemId, options))
          ?.quantity ?? 0,
      add,
      addMany,
      setQuantity,
      remove,
      clear,
      conflictsWith: (restaurantId: string) =>
        state.restaurantId !== null &&
        state.restaurantId !== restaurantId &&
        state.lines.length > 0,
    };
  }, [state, add, addMany, setQuantity, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * The payload shape the order RPCs expect.
 *
 * @param lines Cart lines.
 * @returns An array of `{ item_id, variant_id, quantity }`.
 */

export function cartPayload(
  lines: CartLine[],
): Array<{
  item_id: string;
  quantity: number;
  options: Array<{ choice_id: string; quantity: number }>;
}> {
  return lines.map((line) => ({
    item_id: line.itemId,
    quantity: line.quantity,
    // The shape compute_cart reads. Quantity is always 1 today.
    options: (line.options ?? []).map((option) => ({ choice_id: option.choiceId, quantity: option.quantity })),
  }));
}
